import React, { useEffect, useState } from 'react';
import { Accordion, Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import api from '../api/api';
import { addTrialDays } from '../utils/trialPolicy';
import ClinicManagers from './ClinicManagers';
import TherapistOrganisationModal from './TherapistOrganisationModal';

export default function ClinicOrganisationModal({ clinic, clinics, onClose, onSaved }) {
  const [details, setDetails] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ billing_state: '', start_date: '', trial_end_date: '' });
  const [notice, setNotice] = useState('');
  const [therapist, setTherapist] = useState(null);
  const [showManagers, setShowManagers] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let active = true;
    setError('');
    api.get(`/organisation-clinics/${clinic.id}/`).then(({ data }) => {
      if (active) {
        setDetails(data);
        setForm({ billing_state: data.company_subscription?.billing_state || '', start_date: data.company_subscription?.start_date || '', trial_end_date: data.company_subscription?.trial_end_date || '' });
      }
    }).catch(() => { if (active) setError('Could not load organisation details. Please try again.'); });
    return () => { active = false; };
  }, [clinic.id, refresh]);

  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true); setError(''); setNotice('');
    try {
      const { data } = await api.patch(`/organisation-clinics/${clinic.id}/`, {
        ...form, trial_end_date: form.billing_state === 'trialing' ? form.trial_end_date : null,
      });
      setDetails(data);
      setNotice('Subscription saved.'); onSaved();
    } catch (err) {
      const data = err.response?.data;
      setError(data?.detail || Object.values(data || {}).flat()[0] || 'Could not save the subscription. Please try again.');
    } finally { setSaving(false); }
  };
  const update = (field, value) => { setForm((current) => ({ ...current, [field]: value })); setNotice(''); };
  if (therapist) return <TherapistOrganisationModal companyCycleOnly user={therapist} clinics={clinics}
    onClose={() => setTherapist(null)} onSaved={() => { setRefresh((value) => value + 1); onSaved(); }} />;
  return <Modal show onHide={() => { if (!saving) onClose(); }} size="lg" scrollable>
    <Modal.Header closeButton={!saving}><Modal.Title>{clinic.name}</Modal.Title></Modal.Header>
    <div className="d-flex flex-column overflow-hidden" style={{ minHeight: 0 }}>
      <Modal.Body>
        {error && <Alert variant="danger">{error} {!details && <Button type="button" variant="link" onClick={() => setRefresh((value) => value + 1)}>Retry</Button>}</Alert>}
        {notice && <Alert variant="success" role="status">{notice}</Alert>}
        {!details && !error && <Spinner animation="border" role="status"><span className="visually-hidden">Loading organisation</span></Spinner>}
        {details && <>
          <p className="small text-muted">One subscription cycle applies to all therapists in this company.</p>
          <fieldset disabled={saving}>
            <Form id="company-subscription-form" onSubmit={save}>
            <Form.Group controlId="company-subscription-status" className="mb-3">
              <Form.Label>Subscription status</Form.Label>
              <Form.Select required value={form.billing_state} onChange={(event) => {
                const state = event.target.value;
                setForm({ ...form, billing_state: state, trial_end_date: state === 'trialing' ? form.trial_end_date || addTrialDays(form.start_date) : '' });
                setNotice('');
              }}>
                <option value="">Select status</option><option value="active">Active</option><option value="trialing">In trial</option><option value="sponsored">Sponsored</option><option value="cancelled">Cancelled</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="company-subscription-start" className="mb-3">
              <Form.Label>Subscription start date</Form.Label>
              <Form.Control type="date" required value={form.start_date} onChange={(event) => {
                const start = event.target.value;
                setForm({ ...form, start_date: start, trial_end_date: form.billing_state === 'trialing' && !form.trial_end_date ? addTrialDays(start) : form.trial_end_date });
                setNotice('');
              }} />
            </Form.Group>
            {form.billing_state === 'trialing' && <Form.Group controlId="company-trial-end" className="mb-3">
              <Form.Label>Trial end date</Form.Label>
              <div className="d-flex flex-column flex-sm-row gap-2">
                <Form.Control type="date" required min={form.start_date || undefined} value={form.trial_end_date} onChange={(event) => update('trial_end_date', event.target.value)} />
                <Button type="button" variant="outline-primary" className="text-nowrap" disabled={!form.trial_end_date} onClick={() => update('trial_end_date', addTrialDays(form.trial_end_date, 7))}>+ 1 week</Button>
              </div>
            </Form.Group>}
            </Form>
          </fieldset>
          <Accordion className="mt-4" onSelect={(key) => { if (key === "managers") setShowManagers(true); }}>
            <Accordion.Item eventKey="managers">
              <Accordion.Header>Clinic managers</Accordion.Header>
              <Accordion.Body>{showManagers && <ClinicManagers clinicId={clinic.id} therapists={details.therapists} onSaved={() => { setRefresh((value) => value + 1); onSaved(); }} />}</Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="therapists">
              <Accordion.Header>Therapists ({details.therapists.length})</Accordion.Header>
              <Accordion.Body>
                {!details.therapists.length && <p className="mb-0">No therapists in this clinic.</p>}
                {details.therapists.map((person) => <div key={person.id} className="d-flex flex-column flex-sm-row justify-content-between gap-2 border-bottom py-2">
                  <div style={{ minWidth: 0 }}><strong>{person.first_name} {person.last_name}</strong>{person.is_clinic_manager && <span className="badge ms-2" style={{ backgroundColor: "#ec2873" }}>Clinic manager</span>}<div className="small text-break">{person.email}</div></div>
                  <Button type="button" className="align-self-start" size="sm" variant="outline-secondary" disabled={saving} onClick={() => setTherapist(person)}>Manage membership</Button>
                </div>)}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </>}
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" disabled={saving} onClick={onClose}>Close</Button>
        <Button type="submit" form="company-subscription-form" disabled={saving || !details || !form.billing_state || !form.start_date || (form.billing_state === 'trialing' && (!form.trial_end_date || form.trial_end_date < form.start_date))}>{saving ? 'Saving…' : 'Save subscription'}</Button>
      </Modal.Footer>
    </div>
  </Modal>;
}
